import { ConsentRegime, regimeForCountry } from "@shared/consent";

/**
 * Where the visitor is, for deciding which consent regime applies.
 *
 * Served by the Cloudflare Pages Function at client/functions/geo.js, which
 * reads `request.cf` at the edge. Falls back to the API (which reads the
 * `x-user-country` header the Pages Function already injects) for environments
 * that don't go through Pages: local dev, and the Flutter WebView when it is
 * pointed at a non-Cloudflare origin.
 */

export type Geo = {
  country: string | null;
  region: string | null;
};

export type ResolvedRegime = Geo & { regime: ConsentRegime; gpc: boolean };

/**
 * Global Privacy Control: a browser-level "do not sell or share my personal
 * information" signal.
 *
 * California (CCPA/CPRA), Colorado and Connecticut treat it as a legally
 * binding opt-out that a business must honour without asking again — it is not
 * advisory, and ignoring it is the enforcement action the California AG has
 * actually brought. So in opt-out regions, where our default would otherwise be
 * a grant, GPC turns marketing off before anything loads.
 */
export function globalPrivacyControlEnabled(): boolean {
  return (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;
}

const CACHE_KEY = "oc_geo";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const OVERRIDE_KEY = "oc_geo_override";
const REQUEST_TIMEOUT_MS = 3000;

type CachedGeo = Geo & { t: number };

function readCache(): Geo | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedGeo;
    if (!parsed || typeof parsed.t !== "number") return null;
    if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
    return { country: parsed.country ?? null, region: parsed.region ?? null };
  } catch {
    return null;
  }
}

function writeCache(geo: Geo): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...geo, t: Date.now() }));
  } catch {
    /* private mode — resolving again next load is cheap */
  }
}

/**
 * `?geo=DE` forces a country for the rest of the session. The banner is
 * otherwise untestable from outside the EEA without a VPN, and it only ever
 * affects the person who typed it.
 */
function readOverride(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("geo");
    if (fromUrl) {
      sessionStorage.setItem(OVERRIDE_KEY, fromUrl.toUpperCase());
      return fromUrl.toUpperCase();
    }
    return sessionStorage.getItem(OVERRIDE_KEY);
  } catch {
    return null;
  }
}

async function fetchJson(url: string): Promise<Geo | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });
    // The Vite dev server and the SPA fallback both answer unknown paths with
    // index.html, so a 200 alone proves nothing.
    const contentType = res.headers.get("content-type") ?? "";
    if (!res.ok || !contentType.includes("application/json")) return null;
    const body = await res.json();
    const payload = body?.data ?? body;
    if (!payload || typeof payload !== "object") return null;
    return {
      country: payload.country ? String(payload.country).toUpperCase() : null,
      region: payload.region ? String(payload.region).toUpperCase() : null,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolves the regime, never rejects.
 *
 * An unresolvable location is treated as opt-in: a banner shown to someone who
 * did not strictly need one costs a click, while the reverse is a violation.
 */
export async function resolveRegime(): Promise<ResolvedRegime> {
  const gpc = globalPrivacyControlEnabled();

  const override = readOverride();
  if (override) {
    return { country: override, region: null, regime: regimeForCountry(override), gpc };
  }

  const cached = readCache();
  if (cached) {
    return { ...cached, regime: regimeForCountry(cached.country), gpc };
  }

  const geo = (await fetchJson("/geo")) ?? (await fetchJson("/api/consent/geo"));

  if (!geo) {
    // Not cached: a transient failure must not pin someone to the strict
    // regime for a day.
    return { country: null, region: null, regime: "opt-in", gpc };
  }

  writeCache(geo);
  return { ...geo, regime: regimeForCountry(geo.country), gpc };
}
